import {
  type ProxyRequestMessage,
  type ProxyResponseMessage,
  type ProxyResumePayload,
} from "./ProxyTypes";
import { encodeToBase64, decodeFromBase64 } from "../util";

/**
 * Given a request message received from the proxy, formats it as a multi-line string
 * so that it can be displayed to the user for review and editing.
 */
export function formatRequestText(req: ProxyRequestMessage): string {
  // Format status line; e.g. 'GET /foo?bar=42 HTTP/1.1'
  let text = `${req.method} ${req.path} ${req.http_version}\n`;

  // Append headers line-by-line, immediately after the status line
  text += formatHeaderLines(req.headers);

  // Add a blank line to delineate the end of the headers and the start of the body
  text += "\n\n";

  // Append the request body verbatim
  text += formatBody(req.body);
  return text;
}

export function formatResponseText(res: ProxyResponseMessage): string {
  // Format status line: proxy API does not encode HTTP version for responses
  let text = `HTTP/1.1 ${res.status_code}\n`;

  // Append headers line-by-line, immediately after the status line
  text += formatHeaderLines(res.headers);

  // Add a blank line to delineate the end of the headers and the start of the body
  text += "\n\n";

  // Append the request body verbatim
  text += formatBody(res.body);
  return text;
}

/**
 * Returns the value of the 'Date' header, parsed as a JavaScript Date object, if
 * present. Header lookup is case-insensitive. Returns null if no Date header is present
 * or if the value can not be parsed as a valid Date.
 */
export function parseDateHeader(headers: { [key: string]: string }): Date | null {
  const dateKey = Object.keys(headers).find(s => s.toLowerCase() === "date");
  if (dateKey) {
    const dateString = headers[dateKey];
    const dateValue = new Date(dateString);
    if (!isNaN(dateValue.getDate())) {
      return dateValue;
    }
  }
  return null;
}

/**
 * Given the user-editable text describing an intercepted request, attempts to parse it
 * to a valid payload that can be sent to the proxy in order to resume the request with
 * the user's edits applied.
 */
export function parseRequestText(requestText: string): ProxyResumePayload | null {
  // Parse the start line, e.g. 'GET /foo?bar=42 HTTP/1.1'
  const lines = requestText.split("\n");
  if (!lines) {
    return null;
  }
  const startLineMatch = lines[0].match(/^(\w+) ([^\s]+) (HTTP\/[^\s]+)$/);
  if (!startLineMatch) {
    return null;
  }
  const method = startLineMatch[1];
  const path = startLineMatch[2];
  const http_version = startLineMatch[3];

  // Keep parsing headers until we hit an ensuing blank line - the proxy API uses a JSON
  // object for headers so we lose ordering and handle duplicates by accepting only the
  // last instance
  const headers = {} as { [key: string]: string };
  let hasFinishedHeaders = false;
  let i = 1;
  while (i < lines.length) {
    const line = lines[i].trim();
    if (line) {
      // If we've passed the end of the headers, break: i is now the index of the line
      // that begins the request body
      if (hasFinishedHeaders) {
        break;
      }
      // Otherwise, parse this line as a 'Header-Name: value' pair
      const tokens = line.split(":").map(s => s.trim());
      headers[tokens[0]] = tokens.slice(1).join(":");
    } else {
      // If we've hit a blank line after parsing headers, we're done parsing headers:
      // as soon as we hit a non-blank line, that's the start of our request body
      if (headers) {
        hasFinishedHeaders = true;
      }
    }
    i++;
  }

  const body = lines.slice(i).join("\n");
  return {
    method,
    path,
    http_version,
    headers,
    body_encoded: encodeToBase64(body),
  };
}

/**
 * Given a mapping of header names to values, returns a multi-line string containing all
 * header names and values in the standard HTTP format. The final line is not
 * newline-terminated.
 *
 * When the proxy API serializes request or response headers in a JSON object, we lose
 * the original order of the headers as generated by the browser (for a request) or the
 * sandbox app (for a response). The ordering of headers is not technically significant,
 * but in order to make HTTP messages more human-readable we attempt to sort headers
 * according to specificity, with more general headers like 'Host', 'Accept', 'Referer',
 * etc. appearing first.
 */
function formatHeaderLines(headers: { [key: string]: string }): string {
  // Assign a rank to each line, collecting an array of lines for each discrete rank
  const linesByRank = {} as { [key: number]: string[] };
  for (const [headerName, value] of Object.entries(headers)) {
    const rank = getHeaderRank(headerName);
    if (linesByRank[rank] === undefined) {
      linesByRank[rank] = [];
    }
    linesByRank[rank].push(`${headerName}: ${value}`);
  }

  // Iterate through each rank in order, flattening the list of lines, sorted by
  // alphabetical order within each rank
  const flattenedLines = [] as string[];
  for (const [, lines] of Object.entries(linesByRank).sort(
    (a, b) => parseInt(a[0]) - parseInt(b[0])
  )) {
    for (const line of lines.sort((a, b) => a.localeCompare(b))) {
      flattenedLines.push(line);
    }
  }

  // Return a newline-delimited string
  return flattenedLines.join("\n");
}

/**
 * Given the name of a header, returns an arbitrary integer 'rank' that roughly defines
 * how it should be ordered relative to other headers in the same HTTP message.
 */
function getHeaderRank(headerName: string): number {
  const prefix = headerName.toLowerCase().split("-")[0];
  switch (prefix) {
    case "date":
      return -60;
    case "expires":
      return -50;
    case "host":
      return -40;
    case "user":
      return -30;
    case "accept":
      return -20;
    case "referer":
      return -10;
    case "connection":
      return 0;
    case "upgrade":
      return 10;
    case "if":
      return 20;
    case "cache":
      return 30;
  }
  return 0;
}

/**
 * Given the base64-encoded body of an HTTP request or response, returns the full,
 * decoded, plain-text representation of that body. Defaults to empty-string if no body
 * is present.
 */
function formatBody(bodyBase64?: string): string {
  if (!bodyBase64) {
    return "";
  }
  return decodeFromBase64(bodyBase64);
}
